import { useEffect, useState } from "react";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import SEO from "@/components/SEO";
import { canonical, collectionPageSchema } from "@/utils/seo";
import { getAllTopics } from "@/api/content";
import type { Topic } from "@/types/content";

export default function TopicsPage({ initialData }: { initialData?: Topic[] }) {
  const [topics, setTopics] = useState<Topic[]>(initialData || []);
  const [loading, setLoading] = useState(!initialData || initialData.length === 0);

  useEffect(() => {
    getAllTopics().then((data) => {
      setTopics(data);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <SectionSkeleton type="spinner" />;
  }

  return (
    <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
      <div className="max-w-6xl mx-auto">
        <SEO
          meta={{
            title: "探索主題｜美好時光指南",
            description: "從情侶約會到運動健身，找到你感興趣的主題，發現更多好活動與優質店家。",
            canonical: canonical("/topics"),
            keywords: ["主題探索", "活動主題", "美業主題", "美好時光指南"],
            structuredData: collectionPageSchema("探索主題｜美好時光指南", "從情侶約會到運動健身，找到你感興趣的主題。", "/topics"),
          }}
        />
        <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
          探索主題
        </h1>
        <p className="text-slate-500 mb-10 max-w-2xl">
          從情侶約會到運動健身，找到你感興趣的主題，發現更多好活動
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {topics.map((topic) => (
            <a
              key={topic.id}
              href={`/topics/${topic.slug}`}
              className="group block relative rounded-xl overflow-hidden aspect-[4/3]"
            >
              <img
                loading="lazy"
                src={topic.coverImage}
                alt={topic.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
              <div className="absolute inset-0 flex flex-col justify-end p-5">
                <h2 className="text-xl font-bold text-white mb-1">
                  {topic.title}
                </h2>
                <p className="text-sm text-white/80 line-clamp-2">
                  {topic.description}
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}